<script lang="ts">
import { computed, defineComponent, h, provide, unref } from 'vue';

import { classNames } from '@components/utils';

import {
  makeGroupItemProps,
  makeDisabledProps,
  makeTitleProps,
  makeLazyProps,
  makeTextProps,
  makeTagProps,
  useGroupItem,
  useRender,
  makeReadonlyProps
} from '../../composable';

import CdsExpandablePanelContent from './CdsExpandablePanelContent.vue';
import { CdsExpandablePanelsGroupSymbol } from './CdsExpandablePanels.vue';
import CdsExpandablePanelTitle from './CdsExpandablePanelTitle.vue';
import { makeExpandablePanelTitleProps } from './composable';

export const componentName = 'CdsExpandablePanel';
export default defineComponent({
  name: componentName,
  props: {
    ...makeExpandablePanelTitleProps(),
    ...makeGroupItemProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeTitleProps(),
    ...makeLazyProps(),
    ...makeTextProps(),
    ...makeTagProps()
  },
  emits: {
    'group:selected': (val: { value: boolean }) => true
  },
  setup(props, { slots, expose }) {
    const groupItem = useGroupItem(props, CdsExpandablePanelsGroupSymbol);
    const isDisabled = computed(() => unref(groupItem?.disabled) || props.disabled);

    provide(CdsExpandablePanelsGroupSymbol, groupItem);

    useRender(() => {
      const hasText = !!(slots.text || props.text);
      const hasTitle = !!(slots.title || props.title);

      return h(
        props.tag,
        {
          class: classNames('cds-expandable-panel', {
            'cds-expandable-panel--selected': unref(groupItem.isSelected),
            'cds-expandable-panel--disabled': unref(isDisabled)
          })
        },
        [
          hasTitle &&
            h(
              CdsExpandablePanelTitle,
              {
                key: 'title',
                expandIcon: props.expandIcon,
                collapseIcon: props.collapseIcon,
                hideActions: props.hideActions,
                readonly: props.readonly
              },
              {
                default: () => (slots.title ? slots.title() : props.title)
              }
            ),
          hasText &&
            h(CdsExpandablePanelContent, { key: 'text' }, { default: () => (slots.text ? slots.text() : props.text) }),
          slots.default?.()
        ]
      );
    });

    expose({
      ...groupItem,
      isDisabled
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsExpandablePanel';
</style>
